import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Seo } from "../components/Seo";
import { useAuth, buildLoginUrl } from "../lib/AuthContext";
import {
  fetchMyNotes,
  fetchMyItineraries,
  createMyNote,
  updateMyNote,
  deleteMyNote,
} from "../lib/api";
import {
  NotePencil,
  Plus,
  Trash,
  FloppyDisk,
  X,
  Sparkle,
  SignIn,
  MapPin,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY_DRAFT = { id: null, title: "", body: "", itinerary_id: "" };

export default function MyNotes() {
  const { user, loading } = useAuth();
  const [notes, setNotes] = useState([]);
  const [trips, setTrips] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [draft, setDraft] = useState(null); // null = not editing; object = editing
  const [saving, setSaving] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const tripFilter = searchParams.get("trip") || "";

  // Load notes + trip list whenever user appears or filter changes
  useEffect(() => {
    if (loading || !user) return;
    setFetching(true);
    Promise.all([fetchMyNotes(tripFilter || undefined), fetchMyItineraries()])
      .then(([n, t]) => {
        setNotes(n);
        setTrips(t);
      })
      .catch(() => toast.error("Couldn't load your notes."))
      .finally(() => setFetching(false));
  }, [user, loading, tripFilter]);

  // If the URL says ?new=1 (or ?trip=… with no notes yet) open a fresh draft once.
  useEffect(() => {
    if (loading || !user) return;
    if (searchParams.get("new") === "1") {
      setDraft({ ...EMPTY_DRAFT, itinerary_id: tripFilter });
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading]);

  const filterLabel = useMemo(() => {
    if (!tripFilter) return null;
    const t = trips.find((x) => x.id === tripFilter);
    return t ? t.title : "Selected trip";
  }, [tripFilter, trips]);

  const openNew = () =>
    setDraft({ ...EMPTY_DRAFT, itinerary_id: tripFilter });
  const openEdit = (n) =>
    setDraft({
      id: n.id,
      title: n.title || "",
      body: n.body || "",
      itinerary_id: n.itinerary_id || "",
    });
  const cancelDraft = () => setDraft(null);

  const save = async () => {
    if (!draft) return;
    const title = draft.title.trim();
    if (!title) {
      toast.error("Give your note a title");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title,
        body: draft.body || "",
        itinerary_id: draft.itinerary_id || null,
      };
      let saved;
      if (draft.id) {
        saved = await updateMyNote(draft.id, payload);
        setNotes((prev) => prev.map((n) => (n.id === saved.id ? saved : n)));
        toast.success("Note updated");
      } else {
        saved = await createMyNote(payload);
        setNotes((prev) => [saved, ...prev]);
        toast.success("Note saved");
      }
      setDraft(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the note");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this note?")) return;
    try {
      await deleteMyNote(id);
      setNotes((prev) => prev.filter((n) => n.id !== id));
      toast.success("Note deleted");
    } catch {
      toast.error("Couldn't delete the note");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FAF7F0]">
      <Seo
        title="My Travel Notes · Flycher"
        description="Personal travel notes & write-ups for your saved Flycher itineraries."
        noindex
      />
      <Navbar />
      <main className="flex-1 max-w-5xl mx-auto w-full px-5 sm:px-8 pt-28 pb-20">
        <span className="kicker">— Your travel journal</span>
        <h1 className="font-serif text-4xl sm:text-5xl mt-2 text-slate-900">
          My Travel Notes
        </h1>
        <p className="text-slate-600 mt-3 max-w-2xl">
          Jot down your packing list, deal-breakers, country research, photo prompts or post-trip reflections. Each note can optionally be tied to one of your saved trips.
        </p>

        {loading && (
          <div className="mt-12 text-slate-500 font-mono text-sm">Loading…</div>
        )}

        {!loading && !user && (
          <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
            <div
              className="w-12 h-12 mx-auto rounded-2xl grid place-items-center"
              style={{ background: "#EAF4FB" }}
            >
              <NotePencil size={22} color="#2A7FB8" weight="duotone" />
            </div>
            <h2 className="font-serif text-2xl mt-4 text-slate-900">
              Sign in to write travel notes
            </h2>
            <p className="text-sm text-slate-600 mt-2">
              Notes are saved to your Flycher account so you can pick up where you left off, on any device.
            </p>
            <a
              href={buildLoginUrl("/me/notes")}
              data-testid="notes-signin-cta"
              className="inline-flex items-center gap-2 mt-6 px-5 py-3 rounded-full text-sm font-semibold text-white shadow-md shadow-sky-200/60"
              style={{ background: "#2A7FB8" }}
            >
              <SignIn size={16} weight="bold" /> Continue with Google
            </a>
          </div>
        )}

        {user && (
          <>
            <div className="mt-8 flex flex-wrap items-center gap-3 justify-between">
              <div className="flex flex-wrap items-center gap-2">
                <select
                  data-testid="notes-trip-filter"
                  value={tripFilter}
                  onChange={(e) => {
                    const next = new URLSearchParams(searchParams);
                    if (e.target.value) next.set("trip", e.target.value);
                    else next.delete("trip");
                    setSearchParams(next, { replace: true });
                  }}
                  className="input-base h-11 text-sm w-full sm:w-72"
                >
                  <option value="">All notes</option>
                  {trips.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.title}
                    </option>
                  ))}
                </select>
                {filterLabel && (
                  <span className="text-xs font-mono text-slate-500">
                    · filtered to <strong className="text-[#2A7FB8]">{filterLabel}</strong>
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={openNew}
                data-testid="notes-new-btn"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold text-white shadow-sm"
                style={{ background: "#FF7E4D" }}
              >
                <Plus size={14} weight="bold" /> New note
              </button>
            </div>

            {/* Editor panel */}
            {draft && (
              <div
                data-testid="notes-editor"
                className="mt-6 rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden"
              >
                <div className="px-5 py-3 flex items-center justify-between border-b border-slate-100">
                  <span className="kicker text-[10px]">
                    — {draft.id ? "Editing note" : "New note"}
                  </span>
                  <button
                    type="button"
                    onClick={cancelDraft}
                    aria-label="Cancel"
                    className="text-slate-400 hover:text-slate-700"
                  >
                    <X size={16} weight="bold" />
                  </button>
                </div>
                <div className="p-5 space-y-3">
                  <input
                    data-testid="notes-title-input"
                    value={draft.title}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, title: e.target.value }))
                    }
                    placeholder="Note title — e.g. Vietnam packing list"
                    className="input-base h-12 text-base font-semibold"
                    maxLength={160}
                    autoFocus
                  />
                  <textarea
                    data-testid="notes-body-input"
                    value={draft.body}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, body: e.target.value }))
                    }
                    placeholder="Start writing…  Visa hacks, food bucket-list, photo prompts, post-trip reflections — anything."
                    rows={8}
                    className="input-base text-sm leading-relaxed py-3"
                    maxLength={20000}
                  />
                  <div className="flex flex-wrap items-center gap-3">
                    <select
                      data-testid="notes-trip-link"
                      value={draft.itinerary_id || ""}
                      onChange={(e) =>
                        setDraft((d) => ({
                          ...d,
                          itinerary_id: e.target.value,
                        }))
                      }
                      className="input-base h-11 text-sm w-full sm:w-80"
                    >
                      <option value="">Not linked to a trip</option>
                      {trips.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.title}
                        </option>
                      ))}
                    </select>
                    <div className="text-[11px] text-slate-400 font-mono">
                      {draft.body?.length || 0} / 20000
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-2">
                    <button
                      type="button"
                      onClick={save}
                      disabled={saving}
                      data-testid="notes-save-btn"
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-white shadow-md disabled:opacity-60"
                      style={{ background: "#2A7FB8" }}
                    >
                      <FloppyDisk size={14} weight="bold" />
                      {saving ? "Saving…" : draft.id ? "Update note" : "Save note"}
                    </button>
                    <button
                      type="button"
                      onClick={cancelDraft}
                      className="px-5 py-3 rounded-full text-sm font-medium text-slate-700 hover:bg-slate-100"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* List */}
            {!fetching && notes.length === 0 && (
              <div
                data-testid="notes-empty"
                className="mt-8 rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center"
              >
                <div
                  className="w-10 h-10 mx-auto rounded-xl grid place-items-center"
                  style={{ background: "#FFF1E8" }}
                >
                  <Sparkle size={18} color="#FF7E4D" weight="duotone" />
                </div>
                <p className="text-slate-600 mt-3">
                  No notes yet{tripFilter ? " for this trip" : ""}. Start a new one!
                </p>
              </div>
            )}

            {notes.length > 0 && (
              <div
                className="mt-8 grid sm:grid-cols-2 gap-4"
                data-testid="notes-list"
              >
                {notes.map((n) => (
                  <article
                    key={n.id}
                    data-testid={`note-${n.id}`}
                    className="rounded-2xl bg-white border border-slate-200 hover:border-[#2A7FB8] transition-colors p-5 flex flex-col"
                  >
                    {n.trip ? (
                      <Link
                        to={`/trip/${n.trip.id}`}
                        className="kicker text-[10px] hover:underline w-fit"
                      >
                        — {n.trip.country} · {n.trip.title}
                      </Link>
                    ) : (
                      <span className="kicker text-[10px]">— Personal note</span>
                    )}
                    <h3 className="font-serif text-xl mt-1 text-slate-900 leading-snug line-clamp-2">
                      {n.title}
                    </h3>
                    {n.body && (
                      <p className="text-sm text-slate-600 mt-3 whitespace-pre-wrap line-clamp-6">
                        {n.body}
                      </p>
                    )}
                    <div className="mt-4 flex items-center justify-between">
                      <div className="text-[11px] font-mono text-slate-400 flex items-center gap-1">
                        <MapPin size={12} />
                        {new Date(n.updated_at || n.created_at).toLocaleDateString()}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          data-testid={`note-edit-${n.id}`}
                          onClick={() => openEdit(n)}
                          className="px-3 py-1.5 rounded-full text-xs font-semibold text-[#2A7FB8] hover:bg-[#EAF4FB]"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          data-testid={`note-delete-${n.id}`}
                          onClick={() => remove(n.id)}
                          className="text-slate-400 hover:text-rose-600 transition-colors p-1.5"
                          aria-label="Delete note"
                        >
                          <Trash size={14} weight="duotone" />
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
